import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { TeleprompterMode } from './TeleprompterMode';
import {
  Loader2, Copy, Check, ExternalLink, QrCode, Mic, Video, Trash2,
  Globe, PhoneIncoming, CalendarClock, Eye, Download, SlidersHorizontal, ChevronDown, ChevronRight, Play, Sparkles, X, FlaskConical, RotateCcw, ScrollText, Save, PenLine, Ban,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const CHUNK_SIZE = 2 * 1024 * 1024;
const TIER_EUR_PER_CREDIT = { starter: 5 / 30000, creator: 22 / 100000, pro: 99 / 500000, scale: 330 / 2000000, business: 1320 / 11000000 };
const DEFAULT_VS = { speed: 1.0, stability: 0.5, similarity_boost: 0.75, style: 0.0, use_speaker_boost: true };

const fmtDur = (s) => {
  if (s == null) return '—';
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return m > 0 ? `${m}:${String(sec).padStart(2, '0')} min` : `${sec}s`;
};

const fmtWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}, ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

const SessionRow = ({ s, testId }) => (
  <div className="text-[10px] font-mono text-muted-foreground flex items-center gap-1.5 flex-wrap" data-testid={testId}>
    <span className="text-muted-foreground/60">{fmtWhen(s.started_at)}</span>
    <span>·</span>
    <span className="text-foreground">{fmtDur(s.seconds_on_page)}</span>
    {s.location && <><span>·</span><span>📍 {s.location}</span></>}
    {s.scroll_pct > 0 && <><span>·</span><span>scrolled {Math.round(s.scroll_pct)}%</span></>}
    {s.video_pct > 0 && <><span>·</span><span className="text-sky-400">video {Math.round(s.video_pct)}%</span></>}
    {s.audio_pct > 0 && <><span>·</span><span className="text-sky-400">voice {Math.round(s.audio_pct)}%</span></>}
    {s.website_seconds > 0 && <><span>·</span><span className="text-emerald-400">website {fmtDur(s.website_seconds)}{s.website_pages > 1 ? ` (${s.website_pages} pages)` : ''}</span></>}
    {(s.clicked_phone || s.clicked_email || s.clicked_calendar || s.clicked_website) && (
      <span className="text-emerald-400">
        {[s.clicked_phone && '📞', s.clicked_email && '✉️', s.clicked_calendar && '📅', s.clicked_website && '🌐'].filter(Boolean).join(' ')}
      </span>
    )}
  </div>
);

const EngChip = ({ children, highlight, testId }) => (
  <span data-testid={testId} className={`text-[10px] font-mono rounded-sm px-2 py-1 border ${highlight
    ? 'text-emerald-300 bg-emerald-950/30 border-emerald-800/50'
    : 'text-muted-foreground bg-secondary/40 border-border/60'}`}>
    {children}
  </span>
);


const VoiceSlider = ({ label, hint, value, min, max, step, format, onChange, testId }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="text-[10px] font-mono text-sky-400">{format(value)}</span>
    </div>
    <input type="range" min={min} max={max} step={step} value={value} data-testid={testId}
      onChange={e => onChange(parseFloat(e.target.value))}
      className="w-full h-1 accent-sky-500 cursor-pointer" />
    {hint && <p className="text-[9px] text-muted-foreground/60 mt-0.5">{hint}</p>}
  </div>
);

const MODE_META = {
  audio: { label: 'Voice variant', icon: Mic, cls: 'text-sky-400 border-sky-900/50 bg-sky-950/20' },
  video: { label: 'Video variant', icon: Video, cls: 'text-violet-400 border-violet-900/50 bg-violet-950/20' },
  none: { label: 'Landing page only', icon: Globe, cls: 'text-emerald-400 border-emerald-900/50 bg-emerald-950/20' },
  free: { label: 'Free variant', icon: FlaskConical, cls: 'text-muted-foreground border-border bg-secondary/30' },
};

const OutreachGuide = ({ page }) => {
  const mode = page.variant_media_mode || 'free';
  const meta = MODE_META[mode] || MODE_META.free;
  const Icon = meta.icon;
  const steps = mode === 'audio' ? [
    { key: 'text', label: 'Review & personalize text', done: !!page.audio_key },
    { key: 'voice', label: 'Generate voice (ElevenLabs)', done: !!page.audio_key },
    { key: 'live', label: 'Landing page & QR ready', done: !!page.audio_key },
  ] : mode === 'video' ? [
    { key: 'text', label: 'Review & personalize text', done: !!page.video_key },
    { key: 'video', label: 'Record video (teleprompter)', done: !!page.video_key },
    { key: 'live', label: 'Landing page & QR ready', done: !!page.video_key },
  ] : mode === 'none' ? [
    { key: 'live', label: 'Landing page & QR ready', done: true },
  ] : null;
  const currentIdx = steps ? steps.findIndex(s => !s.done) : -1;
  return (
    <div className="px-5 py-3 border-b border-border" data-testid="outreach-guide">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider border rounded-sm px-2 py-1 ${meta.cls}`}
          data-testid="guide-variant-badge">
          <Icon className="w-3 h-3" /> {meta.label}
        </span>
        {page.experiment_name && (
          <span className="text-[10px] text-muted-foreground font-mono">{page.experiment_name} · {page.variant_name}</span>
        )}
        {steps && currentIdx === -1 && (
          <span className="text-[10px] text-emerald-400 inline-flex items-center gap-1" data-testid="guide-all-done">
            <Check className="w-3 h-3" /> All steps complete
          </span>
        )}
      </div>
      {steps && (
        <div className="flex items-center gap-1.5 mt-2.5 flex-wrap" data-testid="guide-steps">
          {steps.map((s, i) => {
            const state = s.done ? 'done' : (i === currentIdx ? 'current' : 'todo');
            return (
              <React.Fragment key={s.key}>
                {i > 0 && <ChevronRight className="w-3 h-3 text-muted-foreground/40 shrink-0" />}
                <span data-testid={`guide-step-${s.key}`} data-state={state}
                  className={`inline-flex items-center gap-1.5 text-[11px] rounded-sm border px-2 py-1 ${
                    state === 'done' ? 'text-emerald-400 border-emerald-900/40 bg-emerald-950/20'
                    : state === 'current' ? 'text-sky-300 border-sky-800/60 bg-sky-950/30'
                    : 'text-muted-foreground/70 border-border/60 bg-secondary/20'}`}>
                  {s.done
                    ? <Check className="w-3 h-3" />
                    : <span className={`w-3.5 h-3.5 rounded-full border text-[8px] font-mono inline-flex items-center justify-center ${state === 'current' ? 'border-sky-500 text-sky-300' : 'border-muted-foreground/40'}`}>{i + 1}</span>}
                  {s.label}
                </span>
              </React.Fragment>
            );
          })}
        </div>
      )}
      {mode === 'free' && (
        <p className="text-[10px] text-muted-foreground mt-1.5">Free variant — voice, video or plain page: your choice.</p>
      )}
    </div>
  );
};

export const LandingPageCard = ({ letter, canEdit }) => {
  const { isAdmin } = useAuth();
  const [page, setPage] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deletingPage, setDeletingPage] = useState(false);
  const [leads, setLeads] = useState([]);
  const [recentVisits, setRecentVisits] = useState([]);
  const [engagement, setEngagement] = useState(null);
  const [engagementSessions, setEngagementSessions] = useState([]);
  const [engDetails, setEngDetails] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [spokenText, setSpokenText] = useState('');
  const [savingText, setSavingText] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const [rerendering, setRerendering] = useState(false);
  const [teleprompterOpen, setTeleprompterOpen] = useState(false);
  const [teleprompterScript, setTeleprompterScript] = useState('');
  const [teleprompterMode, setTeleprompterMode] = useState(false);
  const [loadingScript, setLoadingScript] = useState(false);
  const [copied, setCopied] = useState(false);
  const [credits, setCredits] = useState(null);
  const [voiceSettings, setVoiceSettings] = useState(DEFAULT_VS);
  const [showVoiceSettings, setShowVoiceSettings] = useState(false);
  const [variants, setVariants] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [applyingIdx, setApplyingIdx] = useState(null);
  const [experiments, setExperiments] = useState([]);
  const [QRCodeCanvas, setQRCodeCanvas] = useState(null);
  const qrRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    import('qrcode.react').then(m => setQRCodeCanvas(() => m.QRCodeCanvas));
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await api.getLandingByLetter(letter.id);
      setPage(r.data.page);
      setLeads(r.data.leads || []);
      setRecentVisits(r.data.recent_visits || []);
      setEngagement(r.data.engagement || null);
      setEngagementSessions(r.data.engagement_sessions || []);
      setSpokenText(r.data.page.spoken_text || '');
    } catch {
      setPage(null);
    }
    setLoading(false);
  }, [letter.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.getLandingCredits().then(r => setCredits(r.data)).catch(() => {});
    api.getLandingSettings().then(r => setVoiceSettings({ ...DEFAULT_VS, ...(r.data.voice_settings || {}) })).catch(() => {});
  }, []);

  const setVs = (k, v) => setVoiceSettings(s => ({ ...s, [k]: v }));

  const handleCreate = async () => {
    setCreating(true);
    try {
      await api.createLandingFromLetter(letter.id);
      await load();
      toast.success('Landing page created');
    } catch (e) {
      // the page may still have been created (slow response / transient network error) — check before showing an error
      try {
        const r = await api.getLandingByLetter(letter.id);
        if (r.data?.page) {
          setPage(r.data.page);
          setLeads(r.data.leads || []);
          setRecentVisits(r.data.recent_visits || []);
          setEngagement(r.data.engagement || null);
          setEngagementSessions(r.data.engagement_sessions || []);
          setSpokenText(r.data.page.spoken_text || '');
          toast.success('Landing page created');
          setCreating(false);
          return;
        }
      } catch { /* really failed */ }
      toast.error(e.response?.data?.detail || 'Failed to create landing page');
    }
    setCreating(false);
  };

  const pageUrl = page ? `${window.location.origin}/m/${page.slug}` : '';

  const handleDeletePage = async () => {
    setDeletingPage(true);
    try {
      await api.deleteLanding(page.id);
      toast.success('Landing page deleted');
      setConfirmDelete(false);
      setPage(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete landing page');
    }
    setDeletingPage(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(pageUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleQrDownload = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `qr-${(page.company_name || page.slug).replace(/[^a-zA-Z0-9äöüÄÖÜß]+/g, '-')}.png`;
    a.click();
  };

  const handleSaveText = async () => {
    setSavingText(true);
    try {
      const r = await api.updateLanding(page.id, { spoken_text: spokenText });
      setPage(r.data);
      toast.success('Text saved');
    } catch { toast.error('Save failed'); }
    setSavingText(false);
  };

  const handleRerender = async () => {
    setRerendering(true);
    try {
      const r = await api.rerenderLanding(page.id);
      setPage(r.data);
      setSpokenText(r.data.spoken_text || '');
      toast.success('Text re-created from the current template — regenerate the voice to update the audio');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to re-create text'); }
    setRerendering(false);
  };

  const handleTeleprompter = async (force = false) => {
    setLoadingScript(true);
    setTeleprompterOpen(true);
    try {
      const r = await api.getTeleprompterScript(page.id, force);
      setTeleprompterScript(r.data.script);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Script generation failed');
      setTeleprompterOpen(false);
    }
    setLoadingScript(false);
  };

  const handleCopyScript = () => {
    navigator.clipboard.writeText(teleprompterScript);
    toast.success('Teleprompter script copied');
  };

  const handlePreviewAudio = async () => {
    setPreviewing(true);
    try {
      const r = await api.previewLandingAudio(page.id, { text: spokenText, voice_settings: voiceSettings });
      const url = URL.createObjectURL(r.data);
      const audio = new Audio(url);
      audio.onended = () => URL.revokeObjectURL(url);
      audio.play();
      toast.success('Preview (first 200 characters)');
    } catch (e) {
      let msg = 'Preview failed';
      try { msg = JSON.parse(await e.response.data.text()).detail; } catch { /* keep default */ }
      toast.error(msg);
    }
    setPreviewing(false);
  };

  const handleGenerateAudio = async () => {
    setGenerating(true);
    try {
      await api.generateLandingAudio(page.id, { text: spokenText, voice_settings: voiceSettings });
      await load();
      api.getLandingCredits().then(r => setCredits(r.data)).catch(() => {});
      toast.success('Voice message generated (ElevenLabs)');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Voice generation failed');
    }
    setGenerating(false);
  };

  const handleSuggestVariants = async () => {
    setSuggesting(true);
    try {
      const r = await api.suggestLandingSentences(page.id);
      setVariants(r.data.suggestions || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI suggestion failed');
    }
    setSuggesting(false);
  };

  const handleApplyVariant = async (v, idx) => {
    setApplyingIdx(idx);
    try {
      const cs = (page.custom_sentence || '').trim();
      const anchor = 'aufgebaut haben.';
      if (cs && spokenText.includes(cs)) {
        const newText = spokenText.replace(cs, v);
        setSpokenText(newText);
        const r = await api.updateLanding(page.id, { spoken_text: newText, custom_sentence: v });
        setPage(r.data);
        setVariants(null);
        toast.success('Sentence replaced — regenerate the voice to update the audio');
      } else if (spokenText.includes(anchor)) {
        const newText = spokenText.replace(anchor, `${anchor} ${v}`);
        setSpokenText(newText);
        const r = await api.updateLanding(page.id, { spoken_text: newText, custom_sentence: v });
        setPage(r.data);
        setVariants(null);
        toast.success('Sentence inserted — regenerate the voice to update the audio');
      } else {
        await api.updateLanding(page.id, { custom_sentence: v });
        await navigator.clipboard.writeText(v).catch(() => {});
        setPage(p => ({ ...p, custom_sentence: v }));
        setVariants(null);
        toast.info('Copied to clipboard — paste it into the text manually (previous sentence not found)');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply variant');
    }
    setApplyingIdx(null);
  };

  useEffect(() => {
    if (page && canEdit) {
      api.getAbExperiments().then(r => setExperiments(r.data.experiments || [])).catch(() => {});
      api.getLandingSettings().then(r => setStdVideo(r.data?.std_video || null)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page?.id, canEdit]);

  const handleUseStandard = async (version) => {
    setUsingStd(version);
    try {
      await api.useStandardVideo(page.id, version);
      await load();
      toast.success(`Standard video (${version === 'hd' ? 'HD' : 'compact'}) is now live on this page`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to apply standard video'); }
    setUsingStd(null);
  };

  const handleAbAssign = async (experimentId, variantId) => {
    try {
      const r = await api.updateLanding(page.id, { experiment_id: experimentId || null, variant_id: variantId || null });
      setPage(r.data);
      toast.success(experimentId ? 'A/B assignment saved' : 'A/B assignment removed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save A/B assignment');
    }
  };

  const isAssigned = !!(page?.experiment_id && page?.variant_id);
  const [stdVideo, setStdVideo] = useState(null);
  const [usingStd, setUsingStd] = useState(null);
  const stdReady = stdVideo?.status === 'ready';
  const mediaMode = page?.variant_media_mode || 'free';
  const videoOnly = isAssigned && mediaMode === 'video';
  const showTextSection = !isAssigned || mediaMode !== 'none';
  const showVoiceControls = !isAssigned || mediaMode === 'free' || mediaMode === 'audio';
  const showVideoSection = !isAssigned || mediaMode === 'free' || mediaMode === 'video';

  const [salutationInput, setSalutationInput] = useState('');
  const [savingSalutation, setSavingSalutation] = useState(false);
  const [savingHandwritten, setSavingHandwritten] = useState(false);
  useEffect(() => { setSalutationInput(page?.salutation || ''); }, [page?.id, page?.salutation]);

  const handwrittenEligible = isAssigned && ['audio', 'video', 'none'].includes(mediaMode);
  const isHandwritten = handwrittenEligible && !!page?.handwritten;
  const handwriteText = 'Auf der Rückseite finden Sie weitere Informationen zu mir und meinen Partnern, die mich auf meinem Weg mit unternehmerischem Rat und zusätzlichem Kapital unterstützen.\n'
    + 'Hinter dem QR-Code verbergen sich zudem eine kurze Videobotschaft an Sie, sowie verschiedene Möglichkeiten, mich zu erreichen. Über eine Rückmeldung (auch wenn sie negativ ist) freue ich mich sehr.\n'
    + 'Vielen Dank!';

  const handleToggleHandwritten = async (checked) => {
    setSavingHandwritten(true);
    try {
      const r = await api.updateLanding(page.id, { handwritten: checked });
      setPage(r.data);
      toast.success(checked
        ? 'Handwritten mode on — the flyer is now QR-only, write the text yourself'
        : 'Handwritten mode off — the flyer includes the printed text again');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setSavingHandwritten(false);
  };

  const handleCopyHandwriteText = () => {
    navigator.clipboard.writeText(handwriteText);
    toast.success('Text copied');
  };

  const handleSaveSalutation = async () => {
    setSavingSalutation(true);
    try {
      const r = await api.updateLanding(page.id, { salutation: salutationInput.trim() });
      setPage(r.data);
      toast.success('Greeting saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save greeting');
    }
    setSavingSalutation(false);
  };

  const handleVideoUpload = async (file) => {
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) { toast.error('Video too large (max 200MB)'); return; }
    setUploadPct(0);
    try {
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      const init = await api.initChunkedProject(file.name, totalChunks);
      const uploadId = init.data.upload_id;
      for (let i = 0; i < totalChunks; i++) {
        const blob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        await api.uploadChunk(uploadId, i, blob);
        setUploadPct(Math.round(((i + 1) / totalChunks) * 90));
      }
      await api.completeLandingVideo(page.id, uploadId);
      setUploadPct(null);
      await load();
      toast.success('Video uploaded — landing page now shows the video');
    } catch (e) {
      setUploadPct(null);
      toast.error(e.response?.data?.detail || 'Video upload failed');
    }
  };

  const handleDeleteVideo = async () => {
    if (!window.confirm('Remove the video? The page falls back to photo + voice message.')) return;
    try {
      await api.deleteLandingVideo(page.id);
      await load();
      toast.success('Video removed');
    } catch { toast.error('Failed'); }
  };

  const handleReoptimize = async () => {
    if (!window.confirm('Re-compress the video from the uncompressed original in higher quality (1080p)? The HD version goes live automatically in a few minutes.')) return;
    try {
      await api.reoptimizeLandingVideo(page.id);
      await load();
      toast.success('Re-optimization started — the HD version replaces the current video in a few minutes');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return null;

  if (!page) {
    return (
      <div className="bg-card border border-border rounded-sm p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="landing-card-empty">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-sky-600/10 border border-sky-600/20 flex items-center justify-center">
            <Globe className="w-5 h-5 text-sky-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">Personal landing page</p>
            <p className="text-xs text-muted-foreground mt-0.5">QR-linked page with a personalized ElevenLabs voice message for {letter.recipient_name || 'the owner'}</p>
          </div>
        </div>
        {canEdit && (
          <Button onClick={handleCreate} disabled={creating} data-testid="landing-create-btn"
            className="bg-sky-700 hover:bg-sky-600 text-white rounded-sm text-xs h-8 px-4">
            {creating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <QrCode className="w-3.5 h-3.5 mr-1.5" />}
            Create landing page
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-sm" data-testid="landing-card">
      <div className="px-5 py-3.5 border-b border-border flex items-center gap-2 flex-wrap">
        <Globe className="w-4 h-4 text-sky-400" />
        <span className="text-sm font-medium text-foreground">Personal landing page</span>
        <span className="text-[10px] font-mono text-muted-foreground inline-flex items-center gap-1 ml-1">
          <Eye className="w-3 h-3" /> {page.visits || 0} scan{page.visits === 1 ? '' : 's'}
          {page.last_visit_at && <span className="text-muted-foreground/60"> · last {new Date(page.last_visit_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>}
        </span>
        {(page.test_visits || 0) > 0 && (
          <span className="text-[10px] font-mono text-emerald-300 border border-emerald-500/40 bg-emerald-500/10 rounded px-1.5 py-0.5 inline-flex items-center gap-1"
            title="Scanned before the letter was mailed — QR & page work. Not counted in any statistics."
            data-testid="landing-test-scan-chip">
            ✓ QR test scan{page.test_visits === 1 ? '' : 's'} ({page.test_visits}) — not counted
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={handleCopy} data-testid="landing-copy-link"
            className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground border border-border rounded-sm px-2.5 py-1.5 transition-colors">
            {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />} Copy link
          </button>
          <a href={`${pageUrl}?preview=1`} target="_blank" rel="noopener noreferrer" data-testid="landing-open-link"
            title="Preview — not counted as scan, no push"
            className="inline-flex items-center gap-1.5 text-[11px] text-sky-400 hover:text-sky-300 border border-sky-900/40 rounded-sm px-2.5 py-1.5 transition-colors">
            <ExternalLink className="w-3 h-3" /> Open
          </a>
          {isAdmin && (
            <button onClick={() => setConfirmDelete(true)} data-testid="landing-delete-page"
              title="Delete landing page"
              className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-red-400 border border-border hover:border-red-900/50 rounded-sm px-2.5 py-1.5 transition-colors">
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="bg-card border-border rounded-sm max-w-sm" data-testid="landing-card-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-400" /> Delete landing page?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            The landing page for <span className="text-foreground font-medium">{page.company_name || page.slug}</span> will be deleted
            permanently — including all scans and responses. Any printed QR code will stop working. The letter itself is kept.
          </p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" onClick={() => setConfirmDelete(false)} className="text-xs h-8 px-3">Cancel</Button>
            <Button onClick={handleDeletePage} disabled={deletingPage} data-testid="landing-card-delete-confirm"
              className="bg-red-600 hover:bg-red-500 text-white rounded-sm font-medium text-xs h-8 px-3">
              {deletingPage ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Delete permanently'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {isAssigned ? (
        <OutreachGuide page={page} />
      ) : (
        <div className="px-5 py-3 border-b border-border bg-amber-950/15 space-y-1.5" data-testid="landing-unassigned-banner">
          <div className="flex items-center gap-2">
            <FlaskConical className="w-3.5 h-3.5 text-amber-400 shrink-0" />
            <span className="text-[11px] text-amber-300 font-medium">In welchen A/B-Test geht dieser Brief? Bitte Variante wählen:</span>
          </div>
          {canEdit && experiments.filter(x => x.status === 'active').map(x => (
            <div key={x.id} className="flex items-center gap-1.5 flex-wrap pl-6">
              <span className="text-[10px] text-muted-foreground">{x.name}:</span>
              {(x.variants || []).map(v => (
                <button key={v.id} onClick={() => handleAbAssign(x.id, v.id)} data-testid={`landing-ab-quick-${v.id}`}
                  className="text-[10px] px-2 py-1 rounded-sm border border-violet-700/50 text-violet-300 hover:bg-violet-600/15 hover:border-violet-500 transition-colors">
                  {v.name}{v.media_mode && v.media_mode !== 'free' ? ` · ${{ video: 'Video', audio: 'Voice', none: 'ohne Medien' }[v.media_mode]}` : ''}
                </button>
              ))}
            </div>
          ))}
          {canEdit && experiments.filter(x => x.status === 'active').length === 0 && (
            <span className="text-[10px] text-muted-foreground pl-6 block">Kein aktiver Test — auf der A/B-Seite (Insights → A/B Testing) anlegen.</span>
          )}
        </div>
      )}

      {page.opted_out_at && (
        <div className="px-5 py-2.5 border-b border-red-900/40 bg-red-950/20 flex items-center gap-2 flex-wrap" data-testid="landing-optout-banner">
          <Ban className="w-3.5 h-3.5 text-red-400" />
          <span className="text-[11px] text-red-300 font-medium">Opt-out — does not want to be contacted again</span>
          {page.optout_reason && (
            <span className="text-[11px] text-red-400/80">· „{page.optout_reason}"{page.optout_note ? ` — ${page.optout_note}` : ''}</span>
          )}
          <span className="ml-auto text-[10px] font-mono text-red-400/60">
            {new Date(page.opted_out_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
          </span>
        </div>
      )}
      {canEdit && (
        <div className="px-5 py-2.5 border-b border-border flex items-center gap-2 flex-wrap" data-testid="landing-ab-row">
          <FlaskConical className="w-3.5 h-3.5 text-violet-400" />
          <span className="text-[11px] text-muted-foreground">A/B test:</span>
          <select value={page.experiment_id || ''} onChange={e => handleAbAssign(e.target.value, null)}
            data-testid="landing-ab-experiment-select"
            className="bg-secondary/40 border border-border rounded-sm text-[11px] text-foreground px-2 py-1 focus:outline-none focus:border-violet-700">
            <option value="">No experiment</option>
            {experiments.filter(x => x.status === 'active' || x.id === page.experiment_id).map(x => (
              <option key={x.id} value={x.id}>{x.name}</option>
            ))}
          </select>
          {page.experiment_id && (
            <select value={page.variant_id || ''} onChange={e => handleAbAssign(page.experiment_id, e.target.value)}
              data-testid="landing-ab-variant-select"
              className="bg-secondary/40 border border-border rounded-sm text-[11px] text-foreground px-2 py-1 focus:outline-none focus:border-violet-700">
              <option value="">Pick variant…</option>
              {(experiments.find(x => x.id === page.experiment_id)?.variants || []).map(v => (
                <option key={v.id} value={v.id}>
                  {v.name}{v.media_mode && v.media_mode !== 'free' ? ` — ${{ video: 'video only', audio: 'voice only', none: 'no media' }[v.media_mode]}` : ''}
                </option>
              ))}
            </select>
          )}
          {page.experiment_id && !page.variant_id && (
            <span className="text-[10px] text-amber-400">Pick a variant to include this letter in the test</span>
          )}
        </div>
      )}

      <div className="p-5 grid grid-cols-1 lg:grid-cols-[auto,1fr] gap-6">
        {/* QR */}
        <div className="flex flex-col items-center gap-2">
          <div ref={qrRef} className="bg-white p-2.5 rounded-sm" data-testid="landing-qr">
            {QRCodeCanvas && <QRCodeCanvas value={pageUrl} size={512} marginSize={2} style={{ width: 120, height: 120 }} />}
          </div>
          <button onClick={handleQrDownload} data-testid="landing-qr-download"
            className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors">
            <Download className="w-3 h-3" /> QR-Code (PNG)
          </button>
          <a href={`${API_BASE}/api/landing/${page.id}/flyer?origin=${encodeURIComponent(window.location.origin)}`}
            target="_blank" rel="noopener noreferrer" data-testid="landing-flyer-download"
            className="inline-flex items-center gap-1.5 text-[11px] text-sky-400 hover:text-sky-300 transition-colors">
            <Download className="w-3 h-3" /> Flyer (PDF{isHandwritten ? ' · QR only' : ''})
          </a>
          {handwrittenEligible && (
            <label className={`inline-flex items-center gap-1.5 text-[11px] cursor-pointer select-none transition-colors ${isHandwritten ? 'text-amber-300' : 'text-muted-foreground hover:text-foreground'}`}
              title="Flyer is generated without printed text (QR only) — you handwrite the message yourself">
              <input type="checkbox" checked={isHandwritten} disabled={!canEdit || savingHandwritten}
                onChange={e => handleToggleHandwritten(e.target.checked)}
                className="accent-amber-500" data-testid="landing-handwritten-toggle" />
              <PenLine className="w-3 h-3" /> Handwritten
            </label>
          )}
          <span className="text-[9px] font-mono text-muted-foreground/60 max-w-[140px] text-center break-all">/m/{page.slug}</span>
        </div>

        {/* voice + video */}
        <div className="space-y-4 min-w-0">
          <div data-testid="landing-greeting-row">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <span className="text-xs font-medium text-foreground">Greeting on the page</span>
              <span className="text-[10px] text-muted-foreground italic" data-testid="landing-greeting-preview">
                shows: „Guten Tag{salutationInput.trim() ? `, ${salutationInput.trim()}` : ''}!"
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input value={salutationInput} onChange={e => setSalutationInput(e.target.value)} disabled={!canEdit}
                placeholder="e.g. Herr Friedrichs — empty = just „Guten Tag!"
                className="h-7 w-72 max-w-full bg-secondary/40 border border-border rounded-sm px-2 text-xs text-foreground focus:outline-none focus:border-sky-700"
                data-testid="landing-salutation-input" />
              {canEdit && (
                <Button onClick={handleSaveSalutation}
                  disabled={savingSalutation || salutationInput.trim() === (page.salutation || '')}
                  data-testid="landing-salutation-save"
                  className="bg-secondary hover:bg-secondary/80 text-foreground border border-border rounded-sm text-[11px] h-7 px-3">
                  {savingSalutation ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                  Save
                </Button>
              )}
            </div>
          </div>

          {isHandwritten && (
            <div className="bg-amber-950/15 border border-amber-900/40 rounded-sm p-3.5" data-testid="landing-handwrite-panel">
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-[10px] uppercase tracking-wider text-amber-300 inline-flex items-center gap-1.5">
                  <PenLine className="w-3 h-3" /> Text to handwrite on the flyer
                </span>
                <button onClick={handleCopyHandwriteText} data-testid="landing-handwrite-copy"
                  className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors">
                  <Copy className="w-3 h-3" /> Copy
                </button>
              </div>
              <pre className="whitespace-pre-wrap font-sans text-xs text-foreground leading-relaxed" data-testid="landing-handwrite-text">{handwriteText}</pre>
              <p className="text-[9px] text-muted-foreground/60 mt-2">The PDF flyer is generated without this text (QR code only) — write it on the flyer by hand. Tracked as the handwritten version of this variant.</p>
            </div>
          )}

          {showTextSection && (
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              {videoOnly ? <Video className="w-3.5 h-3.5 text-muted-foreground" /> : <Mic className="w-3.5 h-3.5 text-muted-foreground" />}
              <span className="text-xs font-medium text-foreground">{videoOnly ? 'Script text (for the video)' : 'Spoken message (ElevenLabs)'}</span>
              {!videoOnly && page.audio_generated_at && (
                <span className="text-[9px] font-mono text-emerald-400">generated {new Date(page.audio_generated_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
              )}
            </div>
            <textarea value={spokenText} onChange={e => setSpokenText(e.target.value)} disabled={!canEdit}
              className="w-full min-h-[110px] bg-secondary/40 border border-border rounded-sm p-3 text-xs text-foreground leading-relaxed resize-y focus:outline-none focus:border-sky-700"
              data-testid="landing-spoken-text" />
            {canEdit && (
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Button onClick={handleSaveText} disabled={savingText || spokenText === page.spoken_text} variant="outline"
                  className="border-border text-muted-foreground hover:text-foreground rounded-sm text-[11px] h-7 px-3" data-testid="landing-save-text">
                  {savingText ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Save text
                </Button>
                <Button onClick={handleRerender} disabled={rerendering} variant="outline"
                  title="Re-create the text from the current template (Landing settings). Keeps the AI-personalized sentence; regenerate the voice afterwards."
                  data-testid="landing-rerender-text"
                  className="border-border text-muted-foreground hover:text-foreground rounded-sm text-[11px] h-7 px-3">
                  {rerendering ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                  From template
                </Button>
                {showVoiceControls && (
                <>
                <Button onClick={handlePreviewAudio} disabled={previewing || generating || !spokenText.trim()} variant="outline"
                  data-testid="landing-preview-audio"
                  className="border-sky-900/50 text-sky-400 hover:text-sky-300 rounded-sm text-[11px] h-7 px-3">
                  {previewing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
                  Preview (200)
                </Button>
                <Button onClick={handleGenerateAudio} disabled={generating || !spokenText.trim()} data-testid="landing-generate-audio"
                  className="bg-sky-700 hover:bg-sky-600 text-white rounded-sm text-[11px] h-7 px-3">
                  {generating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Mic className="w-3 h-3 mr-1" />}
                  {page.audio_key ? 'Regenerate voice' : 'Generate voice'}
                </Button>
                </>
                )}
                <Button onClick={handleSuggestVariants} disabled={suggesting} variant="outline"
                  data-testid="landing-suggest-variants-btn"
                  title="AI suggests 3 alternative personalized sentences about the company"
                  className="border-violet-900/50 text-violet-400 hover:text-violet-300 rounded-sm text-[11px] h-7 px-3">
                  {suggesting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                  3 AI sentence variants
                </Button>
                {showVoiceControls && (
                <>
                <button onClick={() => setShowVoiceSettings(!showVoiceSettings)} data-testid="landing-voice-settings-toggle"
                  className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground border border-border rounded-sm px-2 py-1.5 transition-colors">
                  <SlidersHorizontal className="w-3 h-3" /> Voice settings
                  <ChevronDown className={`w-3 h-3 transition-transform ${showVoiceSettings ? 'rotate-180' : ''}`} />
                </button>
                {credits && (
                  <span data-testid="landing-credits"
                    className={`text-[10px] font-mono ${credits.remaining !== null && spokenText.length > credits.remaining ? 'text-amber-400' : 'text-muted-foreground'}`}>
                    {(() => {
                      const rate = TIER_EUR_PER_CREDIT[credits.tier];
                      const eur = rate ? ` (~${(spokenText.length * rate * 0.92).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €)` : '';
                      return credits.remaining !== null
                        ? <>~{credits.remaining.toLocaleString('de-DE')} credits left · this message ≈ {spokenText.length}{eur}{spokenText.length > credits.remaining && ' — not enough, top up ElevenLabs'}</>
                        : <>this message ≈ {spokenText.length} credits{eur}{credits.permission_missing && ' · balance hidden (API key lacks "User: read" permission)'}</>;
                    })()}
                  </span>
                )}
                </>
                )}
              </div>
            )}
            {canEdit && variants && (
              <div className="mt-3 bg-violet-950/20 border border-violet-900/40 rounded-sm p-3.5" data-testid="landing-variants-panel">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-wider text-violet-300 inline-flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" /> Pick a personalized sentence
                  </span>
                  <div className="flex items-center gap-2">
                    <button onClick={handleSuggestVariants} disabled={suggesting} data-testid="landing-variants-refresh"
                      className="text-[10px] text-violet-400 hover:text-violet-300 transition-colors inline-flex items-center gap-1">
                      {suggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} New suggestions
                    </button>
                    <button onClick={() => setVariants(null)} data-testid="landing-variants-close"
                      className="text-muted-foreground hover:text-foreground transition-colors">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {variants.map((v, i) => (
                    <button key={i} onClick={() => handleApplyVariant(v, i)} disabled={applyingIdx !== null}
                      data-testid={`landing-variant-${i}`}
                      className="w-full text-left text-xs text-foreground leading-relaxed bg-secondary/40 hover:bg-violet-900/20 border border-border/60 hover:border-violet-800/50 rounded-sm px-3 py-2 transition-colors flex items-start gap-2">
                      {applyingIdx === i
                        ? <Loader2 className="w-3.5 h-3.5 mt-0.5 shrink-0 animate-spin text-violet-400" />
                        : <span className="text-[10px] font-mono text-violet-400 mt-0.5 shrink-0">{i + 1}.</span>}
                      {v}
                    </button>
                  ))}
                  {variants.length === 0 && (
                    <p className="text-[11px] text-muted-foreground">No suggestions available — the deal needs a company overview or executive summary first.</p>
                  )}
                </div>
                <p className="text-[9px] text-muted-foreground/60 mt-2">Selecting a variant replaces the current personalized sentence in the text above. Regenerate the voice afterwards.</p>
              </div>
            )}
            {canEdit && showVoiceControls && showVoiceSettings && (
              <div className="mt-3 bg-secondary/30 border border-border/60 rounded-sm p-3.5 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3" data-testid="landing-voice-settings-panel">
                <VoiceSlider label="Speed (pace)" value={voiceSettings.speed} min={0.7} max={1.2} step={0.05}
                  format={v => `${v.toFixed(2)}x`} onChange={v => setVs('speed', v)} testId="vs-speed"
                  hint="0.7 = slower & calmer · 1.2 = faster" />
                <VoiceSlider label="Stability" value={voiceSettings.stability} min={0} max={1} step={0.05}
                  format={v => `${Math.round(v * 100)}%`} onChange={v => setVs('stability', v)} testId="vs-stability"
                  hint="Lower = more lively & emotional · higher = more consistent" />
                <VoiceSlider label="Similarity" value={voiceSettings.similarity_boost} min={0} max={1} step={0.05}
                  format={v => `${Math.round(v * 100)}%`} onChange={v => setVs('similarity_boost', v)} testId="vs-similarity"
                  hint="How closely the clone matches your original voice" />
                <VoiceSlider label="Style exaggeration" value={voiceSettings.style} min={0} max={1} step={0.05}
                  format={v => `${Math.round(v * 100)}%`} onChange={v => setVs('style', v)} testId="vs-style"
                  hint="Amplifies the speaking style — adds excitement, costs stability" />
                <label className="flex items-center gap-2 text-[11px] text-muted-foreground cursor-pointer sm:col-span-2">
                  <input type="checkbox" checked={voiceSettings.use_speaker_boost}
                    onChange={e => setVs('use_speaker_boost', e.target.checked)}
                    className="accent-sky-500" data-testid="vs-speaker-boost" />
                  Speaker boost (stronger similarity, slightly more compute)
                </label>
                <p className="text-[9px] text-muted-foreground/60 sm:col-span-2">Settings are saved as default for future generations when you generate.</p>
              </div>
            )}
            {showVoiceControls && page.audio_key && (
              <audio controls preload="none" className="w-full h-9 mt-3" data-testid="landing-audio-preview"
                src={`${API_BASE}/api/public/landing/${page.slug}/audio?v=${encodeURIComponent(page.audio_generated_at || '')}`} />
            )}
          </div>
          )}

          {isAssigned && mediaMode === 'none' && (
            <div className="bg-emerald-950/20 border border-emerald-900/40 rounded-sm px-3.5 py-3 text-[11px] text-emerald-300 leading-relaxed" data-testid="landing-none-note">
              This variant runs without audio or video — the landing page is ready as is. Download the QR code / flyer and send the letter.
            </div>
          )}

          {showVideoSection && (
          <div className={showTextSection ? 'border-t border-border/60 pt-4' : ''}>
            <div className="flex items-center gap-2 mb-1.5">
              <Video className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs font-medium text-foreground">{videoOnly ? 'Video (teleprompter or upload)' : 'Lip-sync video (optional)'}</span>
              {canEdit && (
                <Button onClick={() => handleTeleprompter(false)} variant="outline" data-testid="landing-teleprompter-btn"
                  title="AI formats the spoken text as a teleprompter script for recording — short lines, pauses and emphasis"
                  className="ml-auto border-sky-900/50 text-sky-400 hover:text-sky-300 rounded-sm text-[10px] h-6 px-2">
                  <ScrollText className="w-3 h-3 mr-1" /> Teleprompter script
                </Button>
              )}
            </div>
            {page.video_key ? (
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[11px] text-emerald-400 font-mono truncate">{page.video_filename}</span>
                {page.video_is_standard && (
                  <span className="inline-flex items-center gap-1 text-[10px] text-sky-300 border border-sky-800/50 bg-sky-950/30 rounded-sm px-1.5 py-0.5" data-testid="landing-video-standard-badge"
                    title="This page uses the central gold-standard video">
                    <Check className="w-2.5 h-2.5" /> Standard video · {page.video_is_standard === 'hd' ? 'HD' : 'Compact'}
                  </span>
                )}
                {!page.video_optimized && (
                  <span className="inline-flex items-center gap-1 text-[10px] text-amber-400 border border-amber-900/40 bg-amber-950/20 rounded-sm px-1.5 py-0.5" data-testid="landing-video-optimizing"
                    title="The video is being compressed for fast loading (H.264, max 720px). This runs automatically in the background.">
                    <Loader2 className="w-2.5 h-2.5 animate-spin" /> Compressing…
                  </span>
                )}
                <a href={`${API_BASE}/api/public/landing/${page.slug}/video`} target="_blank" rel="noopener noreferrer"
                  className="text-[11px] text-sky-400 hover:text-sky-300 inline-flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" /> Preview
                </a>
                <a href={`${API_BASE}/api/landing/${page.id}/video/original`}
                  title="Download the uncompressed original upload" data-testid="landing-download-original"
                  className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 transition-colors">
                  <Download className="w-3 h-3" /> Original
                </a>
                {canEdit && page.video_optimized && !page.video_is_standard && (
                  <button onClick={handleReoptimize} data-testid="landing-reoptimize-video"
                    title="Video too compressed? Re-compress from the original in 1080p HD."
                    className="text-[11px] text-violet-400 hover:text-violet-300 inline-flex items-center gap-1 transition-colors">
                    <Sparkles className="w-3 h-3" /> Re-optimize (HD)
                  </button>
                )}
                {canEdit && stdReady && !page.video_is_standard && (
                  <button onClick={() => handleUseStandard('hd')} disabled={!!usingStd} data-testid="landing-use-standard-inline"
                    title="Replace with the central gold-standard video (HD)"
                    className="text-[11px] text-sky-400 hover:text-sky-300 inline-flex items-center gap-1 transition-colors">
                    {usingStd ? <Loader2 className="w-3 h-3 animate-spin" /> : <Video className="w-3 h-3" />} Use standard video
                  </button>
                )}
                {canEdit && (
                  <button onClick={handleDeleteVideo} data-testid="landing-delete-video"
                    className="text-[11px] text-muted-foreground hover:text-red-400 inline-flex items-center gap-1 transition-colors">
                    <Trash2 className="w-3 h-3" /> Remove
                  </button>
                )}
              </div>
            ) : canEdit ? (
              <div>
                <p className="text-[11px] text-muted-foreground leading-relaxed mb-2">
                  {videoOnly
                    ? 'Record your video directly with the built-in teleprompter (button above) — or generate a lip-sync video in the ElevenLabs studio and upload the MP4 here.'
                    : 'Generate the lip-sync video in the ElevenLabs web studio (Image & Video → LipSync) using the voice message above, then upload the MP4 here. The page will show the video instead of photo + audio.'}
                </p>
                {uploadPct !== null ? (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
                      <div className="h-full bg-sky-600 transition-all" style={{ width: `${uploadPct}%` }} />
                    </div>
                    <span className="text-[10px] font-mono text-muted-foreground">{uploadPct}%</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-wrap">
                    {stdReady && (
                      <>
                        <Button onClick={() => handleUseStandard('hd')} disabled={!!usingStd} data-testid="landing-use-standard-hd"
                          className="bg-sky-700 hover:bg-sky-600 text-white rounded-sm text-[11px] h-7 px-3">
                          {usingStd === 'hd' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Video className="w-3 h-3 mr-1" />}
                          Use standard video (HD)
                        </Button>
                        <Button onClick={() => handleUseStandard('compact')} disabled={!!usingStd} variant="outline" data-testid="landing-use-standard-compact"
                          className="border-sky-800/50 text-sky-400 hover:text-sky-300 rounded-sm text-[11px] h-7 px-3">
                          {usingStd === 'compact' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                          Compact (720p)
                        </Button>
                        <span className="text-[10px] text-muted-foreground">or</span>
                      </>
                    )}
                    <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden"
                      onChange={e => handleVideoUpload(e.target.files?.[0])} data-testid="landing-video-input" />
                    <Button onClick={() => fileRef.current?.click()} variant="outline" data-testid="landing-video-upload-btn"
                      className="border-border text-muted-foreground hover:text-foreground rounded-sm text-[11px] h-7 px-3">
                      <Video className="w-3 h-3 mr-1" /> Upload video
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-[11px] text-muted-foreground">No video uploaded.</p>
            )}
          </div>
          )}

          {engagement && engagement.sessions > 0 && (
            <div className="border-t border-border/60 pt-4" data-testid="landing-engagement">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-foreground">Engagement</span>
                <button onClick={() => setEngDetails(o => !o)} data-testid="landing-eng-details-toggle"
                  className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors">
                  <ChevronDown className={`w-3 h-3 transition-transform ${engDetails ? '' : '-rotate-90'}`} />
                  {engDetails ? 'Details ausblenden' : 'Details'}
                </button>
              </div>
              <p className="mt-2 text-sm text-foreground" data-testid="landing-eng-headline">
                {engagement.max_video_pct != null ? (
                  <>🎬 Video zu <span className={`font-semibold ${engagement.max_video_pct >= 90 ? 'text-emerald-400' : 'text-sky-300'}`}>{engagement.max_video_pct}%</span> gesehen
                    {engagement.sessions > 1 && engagement.avg_video_pct != null ? <span className="text-muted-foreground"> (⌀ {engagement.avg_video_pct}%)</span> : null}</>
                ) : engagement.max_audio_pct != null ? (
                  <>🎧 Sprachnachricht zu <span className={`font-semibold ${engagement.max_audio_pct >= 90 ? 'text-emerald-400' : 'text-sky-300'}`}>{engagement.max_audio_pct}%</span> angehört
                    {engagement.sessions > 1 && engagement.avg_audio_pct != null ? <span className="text-muted-foreground"> (⌀ {engagement.avg_audio_pct}%)</span> : null}</>
                ) : (
                  <>⌀ <span className="font-semibold text-sky-300">{fmtDur(engagement.avg_seconds_on_page)}</span> auf der Seite</>
                )}
                <span className="text-muted-foreground"> · {engagement.sessions} Besuch{engagement.sessions === 1 ? '' : 'e'}</span>
              </p>
              {engDetails && (
              <>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <EngChip testId="landing-eng-time">
                  ⌀ {fmtDur(engagement.avg_seconds_on_page)} on page · longest {fmtDur(engagement.max_seconds_on_page)}
                </EngChip>
                {engagement.max_video_pct != null && (
                  <EngChip testId="landing-eng-video" highlight={engagement.max_video_pct >= 90}>
                    video watched ⌀ {engagement.avg_video_pct}% · max {engagement.max_video_pct}%
                  </EngChip>
                )}
                {engagement.max_audio_pct != null && (
                  <EngChip testId="landing-eng-audio" highlight={engagement.max_audio_pct >= 90}>
                    voice listened ⌀ {engagement.avg_audio_pct}% · max {engagement.max_audio_pct}%
                  </EngChip>
                )}
                {engagement.max_scroll_pct != null && (
                  <EngChip testId="landing-eng-scroll">
                    scrolled ⌀ {engagement.avg_scroll_pct}% · max {engagement.max_scroll_pct}%
                  </EngChip>
                )}
                {engagement.phone_clicks > 0 && (
                  <EngChip testId="landing-eng-phone" highlight>📞 phone tapped ×{engagement.phone_clicks}</EngChip>
                )}
                {engagement.email_clicks > 0 && (
                  <EngChip testId="landing-eng-email" highlight>✉️ email tapped ×{engagement.email_clicks}</EngChip>
                )}
                {engagement.calendar_opens > 0 && (
                  <EngChip testId="landing-eng-calendar" highlight>📅 calendar opened ×{engagement.calendar_opens}</EngChip>
                )}
                {engagement.website_clicks > 0 && (
                  <EngChip testId="landing-eng-website" highlight>🌐 website opened ×{engagement.website_clicks}</EngChip>
                )}
                {engagement.avg_website_seconds != null && (
                  <EngChip testId="landing-eng-website-time" highlight>
                    🌐 website read ⌀ {fmtDur(engagement.avg_website_seconds)} · longest {fmtDur(engagement.max_website_seconds)}
                  </EngChip>
                )}
              </div>
              {engagementSessions.length > 0 && (
                <div className="mt-2.5 space-y-1" data-testid="landing-eng-sessions">
                  <span className="text-[9px] uppercase tracking-wider text-muted-foreground/70">Individual visits, newest first</span>
                  {engagementSessions.slice(0, 5).map((sess, i) => (
                    <SessionRow key={i} s={sess} testId={`landing-eng-session-${i}`} />
                  ))}
                </div>
              )}
              </>
              )}
            </div>
          )}

          {recentVisits.length > 0 && (
            <div className="border-t border-border/60 pt-4" data-testid="landing-visits">
              <span className="text-xs font-medium text-foreground">Recent scans</span>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {recentVisits.map(v => (
                  <span key={v.id} className={`text-[10px] font-mono rounded-sm px-2 py-1 border ${v.is_test ? 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10' : 'text-muted-foreground bg-secondary/40 border-border/60'}`}>
                    {v.is_test ? 'TEST · ' : ''}{new Date(v.at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    {v.geo && (v.geo.city || v.geo.cc) ? ` · 📍 ${v.geo.city || v.geo.region || ''}${v.geo.city && v.geo.cc ? `, ${v.geo.cc}` : v.geo.cc || ''}` : ''}
                  </span>
                ))}
              </div>
            </div>
          )}

          {leads.length > 0 && (
            <div className="border-t border-border/60 pt-4" data-testid="landing-leads">
              <span className="text-xs font-medium text-foreground">Responses ({leads.length})</span>
              <div className="mt-2 space-y-1.5">
                {leads.map(l => (
                  <div key={l.id} className="flex items-start gap-2.5 bg-secondary/30 border border-border/60 rounded-sm px-3 py-2">
                    {l.type === 'callback'
                      ? <PhoneIncoming className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                      : <CalendarClock className="w-3.5 h-3.5 text-sky-400 mt-0.5 shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-xs text-foreground">
                        <span className="font-medium">{l.name}</span>
                        {l.company && <span className="text-muted-foreground"> · {l.company}</span>}
                        {l.phone && <span className="text-muted-foreground"> · {l.phone}</span>}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {l.type === 'callback' ? 'Callback request' : 'Appointment request'}
                        {l.preferred_time && ` — ${l.preferred_time}`}
                        <span className="font-mono text-muted-foreground/60"> · {new Date(l.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={teleprompterOpen} onOpenChange={setTeleprompterOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[85vh] flex flex-col" data-testid="teleprompter-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground text-base flex items-center gap-2">
              <ScrollText className="w-4 h-4 text-sky-400" /> Teleprompter script — {page?.company_name || ''}
            </DialogTitle>
          </DialogHeader>
          <p className="text-[11px] text-muted-foreground shrink-0">
            <span className="font-mono">•</span> = short breath pause · <span className="font-mono">— — —</span> = deliberate pause (1–2s) · UPPERCASE = emphasize. Read slower than feels natural.
          </p>
          <div className="flex-1 min-h-0 overflow-y-auto bg-secondary/30 border border-border rounded-sm p-5">
            {loadingScript ? (
              <div className="flex items-center justify-center py-14">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <pre className="whitespace-pre-wrap font-sans text-[15px] leading-loose text-foreground" data-testid="teleprompter-text">{teleprompterScript}</pre>
            )}
          </div>
          <div className="flex items-center justify-between gap-2 shrink-0 flex-wrap">
            <Button variant="outline" onClick={() => handleTeleprompter(true)} disabled={loadingScript}
              data-testid="teleprompter-regenerate"
              className="border-border text-muted-foreground hover:text-foreground rounded-sm text-[11px] h-8 px-3">
              <Sparkles className="w-3 h-3 mr-1" /> Regenerate
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleCopyScript} disabled={loadingScript || !teleprompterScript}
                data-testid="teleprompter-copy"
                className="border-border text-muted-foreground hover:text-foreground rounded-sm text-[11px] h-8 px-3">
                <Copy className="w-3 h-3 mr-1" /> Copy script
              </Button>
              <Button onClick={() => { setTeleprompterOpen(false); setTeleprompterMode(true); }} disabled={loadingScript || !teleprompterScript}
                data-testid="teleprompter-start"
                className="bg-sky-700 hover:bg-sky-600 text-white rounded-sm text-[11px] h-8 px-4">
                <Play className="w-3 h-3 mr-1" /> Start teleprompter
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {teleprompterMode && (
        <TeleprompterMode script={teleprompterScript} page={page} onVideoSaved={load}
          onClose={() => { setTeleprompterMode(false); setTeleprompterOpen(true); }} />
      )}
    </div>
  );
};
